/**
 * "Add person" — search the user directory by name or email and enable them for the connector
 * with one click. This is the same typeahead the Looped-In drawer and code assignment use, so an
 * administrator never has to know (or type) a user id.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch, IconUserPlus, IconCheck } from '@tabler/icons-react';
import { Stack, Group, Text, TextInput, Badge, Card, Loader, Button, Modal, Alert, EmptyState } from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useUserService, useConnectorService } from '@actschurch/ccms-sdk';
import type { UserModelView } from '@actschurch/ccms-sdk';
import { PersonCell } from './components';
import { KEYS } from './keys';

export function AddPersonModal({ opened, onClose, enabledIds }: { opened: boolean; onClose: () => void; enabledIds: Set<string> }) {
  const userService = useUserService();
  const connector = useConnectorService();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [term] = useDebouncedValue(search.trim(), 350);

  const { data: results = [], isFetching, error } = useQuery({
    queryKey: ['users', 'search', 'connector-add', term],
    queryFn: () => userService.search({ searchTerm: term, page: 1, pageSize: 25 }),
    enabled: opened && term.length >= 2,
    staleTime: 60_000,
  });

  const add = useMutation({
    mutationFn: (u: UserModelView) => connector.addPerson(u.id!),
    onSuccess: (person) => {
      toast.success(`${person.name} can now connect`, 'It takes effect the next time they sign in to CCMS.');
      queryClient.invalidateQueries({ queryKey: KEYS.people });
    },
    onError: (e) => toast.error('Could not add the person', e instanceof Error ? e.message : 'Try again.'),
  });

  const close = () => { setSearch(''); onClose(); };

  return (
    <Modal opened={opened} onClose={close} title="Add a person to the connector" size="lg">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Search by name or email. Adding someone puts them in the <b>ConnectorUser</b> role; what they can then do through an AI assistant is still limited by their own CCMS rights.
        </Text>
        <TextInput
          autoFocus
          placeholder="Start typing a name or email…"
          leftSection={<IconSearch size={16} />}
          rightSection={isFetching ? <Loader size="xs" /> : null}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        {error && <Alert color="red" title="Search failed">{error instanceof Error ? error.message : 'Try again.'}</Alert>}
        {term.length < 2 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">Type at least two characters to search the directory.</Text>
        ) : !isFetching && results.length === 0 ? (
          <EmptyState title="No one matches" description="Try a different spelling, or search by email." size="sm" />
        ) : (
          <Stack gap="xs" style={{ maxHeight: 420, overflowY: 'auto' }}>
            {results.filter((u) => u.id).map((u) => {
              const already = enabledIds.has(u.id!);
              const busy = add.isPending && add.variables?.id === u.id;
              return (
                <Card key={u.id!} withBorder radius="md" p="sm">
                  <Group justify="space-between" wrap="nowrap">
                    <PersonCell name={`${u.firstName ?? ''} ${u.surname ?? ''}`.trim() || u.userName} email={u.email} avatar={u.thumbnail ?? u.avatar} />
                    <Group gap="xs" wrap="nowrap">
                      {(u.roles ?? []).filter((r) => r !== 'ConnectorUser').slice(0, 2).map((r) => <Badge key={r} size="xs" variant="outline" color="gray">{r}</Badge>)}
                      {already ? (
                        <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>Enabled</Badge>
                      ) : (
                        <Button size="compact-sm" leftSection={<IconUserPlus size={14} />} loading={busy} onClick={() => add.mutate(u)}>Add</Button>
                      )}
                    </Group>
                  </Group>
                </Card>
              );
            })}
          </Stack>
        )}
        <Group justify="flex-end"><Button variant="default" onClick={close}>Done</Button></Group>
      </Stack>
    </Modal>
  );
}
